import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { apiService } from './services/api';
import { resolveEnvForToken, isLocalhost, redirectToPollyLogin, clearCognitoTokens, getCognitoBearerToken } from './utils/auth';
import AuthForm from './components/Auth/AuthForm';
import SessionExpiredModal from './components/Auth/SessionExpiredModal';
import KGList from './components/KG/KGList';
import KGDetails from './components/KG/KGDetails';
import VersionDetails from './components/Version/VersionDetails';
import InstancesPage from './components/Instance/InstancesPage';
import AuditLog from './components/Audit/AuditLog';
import Analytics from './components/Analytics/Analytics';
import Layout from './components/Layout/Layout';
import './App.css';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'unauthorized';

interface UserInfo {
  email: string;
  firstName: string;
  lastName: string;
  hasGdsLicense: boolean;
}

function UnauthorizedScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="app-unauthorized">
      <div className="app-unauthorized__card">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8E42EE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <h2 className="app-unauthorized__title">Access denied</h2>
        <p className="app-unauthorized__desc">
          Your account does not have the required KG admin permissions to use KG Manager.
          Contact your Polly administrator to request <code>POLLY_KG_ORG_ADMIN</code> or <code>POLLY_KG_GDS_ORG_ADMIN</code> access.
        </p>
        <button className="app-unauthorized__btn" onClick={onLogout}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function App() {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionExpiredRef = useRef(false);

  useEffect(() => {
    const env = resolveEnvForToken();

    // Initialize the API service. On deployed envs, any 401 mid-session means
    // the token expired — redirect to Polly login so the user re-authenticates
    // and is returned here. On localhost, never redirect on 401: the proxy
    // handles auth and a 401 just means the session is gone; components show
    // their own error state.
    apiService.initialize(env, isLocalhost() ? undefined : () => {
      sessionExpiredRef.current = true;
      setSessionExpired(true);
    });

    // On localhost, Cognito cookies are set on .elucidata.io and are NOT
    // readable via document.cookie on localhost — so getCognitoBearerToken()
    // always returns null. The CRA proxy injects the Bearer token from the
    // browser's cookie headers automatically. Skip the cookie check entirely
    // and go straight to authenticated; the proxy handles auth transparently.
    if (isLocalhost()) {
      setStatus('authenticated');
      return;
    }

    // On deployed envs: call /auth/users/me — single source of truth.
    // withCredentials sends the Cognito cookie; if 401, the on401 above redirects.
    apiService.fetchUserProfile()
      .then(({ allowed, hasGdsLicense, email, firstName, lastName }) => {
        if (!allowed) {
          setStatus('unauthorized');
          return;
        }
        setUser({ email, firstName, lastName, hasGdsLicense });
        setStatus('authenticated');
      })
      .catch(() => {
        // If a 401 already fired the session-expired modal, do nothing.
        if (sessionExpiredRef.current) return;

        // If there's no valid token at all, the user is not authenticated —
        // redirect to login rather than showing the broken app.
        if (!getCognitoBearerToken()) {
          redirectToPollyLogin(resolveEnvForToken());
          return;
        }

        // Network / server error but token exists — fall through to the app.
        setStatus('authenticated');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    const env = apiService.getEnv() ?? resolveEnvForToken();
    apiService.reset();
    sessionStorage.clear();
    clearCognitoTokens();
    redirectToPollyLogin(env);
  };

  if (sessionExpired) {
    return <SessionExpiredModal env={apiService.getEnv() ?? resolveEnvForToken()} />;
  }

  if (status === 'loading') {
    return <div className="app-loading"><div className="app-loading-spinner" /></div>;
  }

  if (status === 'unauthorized') {
    return <UnauthorizedScreen onLogout={handleLogout} />;
  }

  if (status === 'unauthenticated') {
    return <AuthForm onAuthenticated={() => window.location.reload()} />;
  }

  return (
    <Router>
      <Layout onLogout={handleLogout} user={user}>
        <Routes>
          <Route path="/" element={<Navigate to="/kg" replace />} />
          <Route path="/kg" element={<KGList hasGdsLicense={user?.hasGdsLicense ?? false} />} />
          <Route path="/instances" element={<InstancesPage />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/kg/:kgId" element={<KGDetails hasGdsLicense={user?.hasGdsLicense ?? false} />} />
          <Route path="/kg/:kgId/version/:versionId" element={<VersionDetails />} />
          <Route path="*" element={<Navigate to="/kg" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;
